/**
 * Shared detail-pane primitives for System Status Frame 2 / Frame 3 (Flow L;
 * ADR-035 / S6.4-S6.5).
 *
 * The consolidated, scrollable, **no-sub-tabs** detail surface per ADR-024 §4:
 * sticky section headers + the small rows they contain (health-rollup mini
 * states, resource lines with honest "not collected", domain-metric rows, a
 * trend sparkline seed) + the shared status pill. Pure presentational; no data
 * fetching. Reused by the component detail (Frame 2), the interface detail, and
 * the connection detail (Frame 3).
 *
 * Two cross-cutting affordances live here because all three panes need them and
 * a per-pane copy would drift (CLAUDE.md §4.4):
 *
 *   - an optional ``description`` on every row, disclosed through the single
 *     shared :func:`DescriptionTooltip` primitive (REQ-STATUS-056 /
 *     REQ-UI-133). It carries the fixed health-row texts
 *     (``HEALTH_ROW_DESCRIPTIONS``) and the metric catalog's descriptions;
 *   - :func:`StatusSectionsProvider`, the **session-local** section
 *     expand/collapse store — captured into the active Profile when the
 *     operator saves settings as a Profile, and re-applied on load
 *     (REQ-UI-131).
 *
 * Cross-references:
 *   - Flow L §4 (component detail) / §5 (connection detail) / §10 (a11y).
 *   - ADR-024 §4 (one consolidated pane, sticky headers, no sub-tabs).
 *   - REQ-UI-046 (status palette, not colour alone); REQ-UI-059 (keyboard/landmarks).
 *   - REQ-STATUS-056 / REQ-UI-133 (row descriptions); REQ-UI-131 (session collapse).
 */
import { useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { cn } from "@/lib/utils";
import { rollupStateMeta, type RollupState } from "@/system-status/status-model";
import { DescriptionTooltip } from "@/components/ui/tooltip";
import { ROLLUP_BAND, type BandVocabulary } from "@/system-status/band-vocabulary";
import {
  EntityDetailsSectionsProvider,
  useEntityDetailsSectionsContext,
  type EntityDetailsSectionsContextValue,
} from "@/entity-details/entity-details-sections-context";
import {
  SECTION_IDS,
  STATUS_ENTITY_DETAIL_TYPES,
  claimStatusSectionStoreId,
  publishStatusSectionOverrides,
  retractStatusSectionOverrides,
  statusSectionExpansionFromDocument,
} from "@/entity-details/use-section-visibility";
import { PreferencesContext } from "@/preferences/preferences-context";
import type { SetPreferencesResult } from "@/preferences/preferences-context";

/**
 * The status-badge shape: **one size and one alignment, everywhere** (architect,
 * 2026-08-01).
 *
 * Exported because the chrome's :func:`SystemRollupBadge` is a *different
 * component* — it carries `role="status"`, `aria-live`, a `stale` state that is
 * not a :type:`RollupState`, and uppercase type — but must be the same shape.
 * It previously hand-duplicated this class string, which is precisely the
 * CLAUDE.md §4.4 duplication that let the two drift apart in width. Sharing the
 * constant rather than the component keeps them in step without pretending they
 * are the same thing.
 *
 * # The two layout decisions in here
 *
 * ``min-w-[5.5rem]`` — 88 px, measured against the repo's bundled DM Sans 400 at
 * ``text-meta`` (11 px), where the widest of the five :type:`RollupState` labels
 * is "Unhealthy" at ~83.3 px ("Degraded" ~82.1 px just behind). Sized to the
 * longest label so the floor is never the thing that truncates, and stated as a
 * floor rather than a fixed width so the chrome's longer uppercase labels still
 * render whole. Two things invalidate the number and must move it: a change to
 * the type scale, and reconciling with ``design-system.md``'s uppercase + 0.04em
 * tracking, which this pill does not currently apply.
 *
 * A ``min-width`` rather than a ``max-content`` grid column, deliberately: these
 * panes update live, and a grid column re-derives its width on every state flip
 * — so a component going Healthy → Unhealthy would shift the whole column *mid
 * incident*, which is motion an operator reads as meaning in the one moment it
 * does not.
 *
 * ``justify-center`` — the slack sits either side of the dot+label pair rather
 * than all after it. The alternative (``justify-start``, dots aligned down the
 * column) was shipped first and rejected: it left every label starting at a
 * different distance from the pill's own edge, so the badges read as
 * inconsistently padded rather than as one control at one size.
 */
export const STATUS_PILL_CLASS =
  "inline-flex min-w-[5.5rem] items-center justify-center gap-1.5 rounded-full border border-border px-2 py-0.5 text-meta";

/**
 * A small status pill: distinct mark + coloured text label (REQ-UI-046).
 *
 * ``testId`` overrides the default ``status-pill-<state>`` id. A pill is the
 * single way every rollup in the Selection Detail is rendered (architect,
 * 2026-08-01), so several appear in one pane — the header's, each health-rollup
 * row's — and a state-derived id alone would match all of them at once. Callers
 * that own a row pass their own row-scoped id; the header and the roster rows,
 * which are one-per-pane, keep the default.
 */
export function StatusPill({
  state,
  testId,
  className,
  title,
}: {
  state: RollupState;
  /** Row-scoped test id; defaults to ``status-pill-<state>``. */
  testId?: string;
  /**
   * Hover text — used for the **fault cause**, on the element whose state the
   * cause explains (architect: the cause rides the badge, never an inline line).
   */
  title?: string;
  /**
   * **Layout only** — width and alignment, for a caller that stacks pills in a
   * column (:func:`MiniStateRow`). Deliberately not a colour hook: the state
   * palette is :func:`rollupStateMeta`'s job, and a free-form class escape hatch
   * is exactly how a caller would accidentally repaint a status — which is what
   * REQ-UI-046 and the ``.status-palette`` block exist to prevent.
   */
  className?: string;
}): React.ReactElement {
  const meta = rollupStateMeta(state);
  return (
    <span
      data-testid={testId ?? `status-pill-${state}`}
      className={cn(STATUS_PILL_CLASS, className)}
      title={title}
    >
      <span
        aria-hidden="true"
        className="inline-block h-2 w-2 rounded-full"
        style={{ background: meta.dotColor }}
      />
      <span className={meta.textClass}>{meta.label}</span>
    </span>
  );
}

/**
 * A section of the consolidated detail pane — a **sticky** header over its body
 * (ADR-024 §4). The header is a labeled heading so the section is reachable /
 * announceable (REQ-UI-059). ``id`` scopes the aria labelling.
 */
export function DetailSection({
  id,
  title,
  children,
}: {
  id: string;
  title: string;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <section aria-labelledby={`${id}-heading`} data-testid={`detail-section-${id}`}>
      <h4
        id={`${id}-heading`}
        className="sticky top-0 z-10 border-b border-border bg-bg-surface px-4 py-1.5 text-meta uppercase tracking-wide text-text-secondary"
      >
        {title}
      </h4>
      <div className="px-4 py-3">{children}</div>
    </section>
  );
}

/**
 * One health-rollup input row (storyboard §4 section 2 / §5) — the operator
 * sees *why* the stoplight is what it is: a label, a **rollup badge**, an
 * optional detail note, and an optional description.
 *
 * # One rollup, one badge (architect, 2026-08-01)
 *
 * The row used to render its state as bare coloured text while the pane's
 * header and its "Reporting components" rows rendered a :func:`StatusPill`.
 * Three renderings of one concept is a legibility cost an operator pays on
 * every glance, so the row now renders the same badge as everything else and
 * drops its own leading dot — the badge already carries the distinct mark
 * REQ-UI-046 requires beside the colour.
 *
 * # Why the badge is a fixed shape
 *
 * The rows stack, so their badges read as a **column**, and sized to their own
 * labels they left-ragged — "Healthy" visibly narrower than "Unhealthy". That
 * shared floor now lives on :data:`STATUS_PILL_CLASS` rather than being passed
 * in from here: this row was the first caller to need it, but it was never the
 * only one, and owning it at the call site left the panel header, the Reporting
 * components roster and the connection drawer still rendering a narrower pill
 * (architect, 2026-08-01). See that constant for the measurement, for why it is
 * a ``min-width`` rather than a grid column, and for why the content centres.
 *
 * # Why the row wraps
 *
 * ``flex-wrap`` with a ``basis-full`` detail is what puts the badge on the
 * **right**. Without it the full-basis detail consumed the row's free space, the
 * badge's ``ml-auto`` had nothing left to distribute, and the state collapsed
 * back against the label — which is why "Process" and "Serving" (which always
 * carried a detail) sat left while "Overall" (usually detail-free) sat right.
 * Wrapping gives the detail its own line and the badge a stable right edge.
 *
 * ``testId`` is **required and independent of ``label``** (REQ-STATUS-027
 * defect (c)). The id used to be derived from the display text, so the
 * "Ready" → "Serving" / "Driver" → "Overall" renames would have retargeted
 * every selector at once — and a suite whose selectors match nothing goes
 * green. Requiring the prop makes the compiler, not review, the thing that
 * catches a new row with no stable id.
 */
export function MiniStateRow({
  testId,
  label,
  state,
  detail,
  description,
}: {
  /** Stable test id, decoupled from the visible label (REQ-STATUS-027). */
  testId: string;
  label: string;
  state: RollupState;
  detail?: string;
  /**
   * Explanatory text for the row, disclosed on demand through the shared
   * tooltip primitive (REQ-STATUS-056 / REQ-UI-133). The health rows' wording
   * is fixed by :data:`HEALTH_ROW_DESCRIPTIONS`, not by this primitive.
   */
  description?: string;
}): React.ReactElement {
  return (
    <div className="flex items-center gap-x-2 py-0.5 text-body-sm" data-testid={testId}>
      <span className="text-text-primary">{label}</span>
      {description !== undefined && (
        <DescriptionTooltip testId={testId} label={label} description={description} />
      )}
      {/* Row-scoped id: several pills share one pane, so the default
          `status-pill-<state>` would match the header's as well as this one.

          The fault cause rides the BADGE's tooltip rather than its own line
          (architect, 2026-08-01). Inline, it forced the row to wrap — which is what
          pushed "Process" and "Serving" left while the detail-free "Overall" sat
          right, the inconsistency this fixes. On the badge it costs no vertical
          space at all, so every rollup row is one line with its badge on the right,
          and the cause is one hover away on exactly the element whose state raised
          it. `tabIndex` keeps it reachable without a pointer (REQ-UI-059). */}
      <span
        className="ml-auto flex-none"
        {...(detail !== undefined && detail !== ""
          ? { title: detail, tabIndex: 0, "data-testid": `${testId}-detail` }
          : {})}
      >
        <StatusPill state={state} testId={`${testId}-state`} />
      </span>
    </div>
  );
}

/**
 * One resource-utilization row; a ``null`` value renders an honest "not
 * collected" (storyboard §4 section 3 / OQ-4 — Postgres-container + nginx/UI
 * CPU/mem are a host-agent tier, not psutil-solvable). Renders ``<dt>``/``<dd>``
 * so it sits inside a definition list.
 */
export function ResourceRow({
  testId,
  label,
  value,
  suffix = "",
  description,
  qualifier,
  breach,
  uncollectedNote,
}: {
  /**
   * Stable test id. Defaults to ``resource-${label}`` for the callers that
   * predate the prop.
   *
   * ⚠ That default is the REQ-STATUS-027(c) trap in waiting: the Phase-E metric
   * catalog relabels ``ResourceMetrics`` fields from deployment configuration
   * (`resource[<field_name>]`), at which point a label-derived id differs
   * between deployments. Phase E must pass the wire field name explicitly
   * (REQ-STATUS-057).
   */
  testId?: string;
  label: string;
  value: number | null | undefined;
  suffix?: string;
  /** Catalog description, disclosed through the shared tooltip (REQ-STATUS-056). */
  description?: string;
  /**
   * Short muted qualifier rendered after the value — the denominator disclosure
   * a percentage needs to be readable ("of host"). REQ-STATUS-006 requires the
   * basis to be visible because "40%" of a 2-core quota and "40%" of a 64-core
   * host are different claims; the caller supplies the wording so this
   * primitive stays free of resource-metric semantics.
   */
  qualifier?: string;
  /**
   * The metric's contribution to the rollup, when it is one of the few that can
   * drive it (:func:`metricThresholdState`). Renders the value in that state's
   * colour, so the number pushing a component off green is visible in the pane
   * rather than only in the header badge's tooltip.
   */
  breach?: "degraded" | "unhealthy" | null;
  /**
   * Why this reading is absent, when the absence is **structural** rather than a
   * gap. A bare "not collected" on four consecutive rows reads as broken
   * telemetry; the qualifier says it is a category difference (see
   * :data:`COTS_RESOURCE_NOTE`).
   */
  uncollectedNote?: string;
}): React.ReactElement {
  const collected = value !== null && value !== undefined;
  const rowId = testId ?? `resource-${label}`;
  return (
    <>
      <dt className="flex items-center gap-1 text-text-secondary">
        {label}
        {description !== undefined && (
          <DescriptionTooltip testId={rowId} label={label} description={description} />
        )}
      </dt>
      <dd
        data-testid={rowId}
        data-breach={breach ?? undefined}
        className={
          collected
            ? breach === "unhealthy"
              ? "font-medium text-meridian-status-unhealthy"
              : breach === "degraded"
                ? "font-medium text-meridian-status-degraded"
                : "text-text-primary"
            : "text-text-muted"
        }
      >
        {collected ? `${Math.round(value)}${suffix}` : "not collected"}
        {collected && qualifier !== undefined && (
          <span data-testid={`${rowId}-qualifier`} className="ml-1 text-text-muted">
            {qualifier}
          </span>
        )}
        {!collected && uncollectedNote !== undefined && (
          <span data-testid={`${rowId}-uncollected-note`} className="ml-1 text-text-muted">
            {uncollectedNote}
          </span>
        )}
      </dd>
    </>
  );
}

/**
 * One domain / throughput / error metric row (label + value).
 *
 * ``testId`` is **required and independent of ``label``** for the same reason as
 * :func:`MiniStateRow` (REQ-STATUS-027 defect (c)), and with a second one ahead
 * of it: the Phase-E metric-label catalog makes ``label`` a *deployment
 * configuration* string, so an id derived from it would differ between
 * deployments.
 */
export function MetricRow({
  testId,
  label,
  value,
  description,
  breach,
  detail,
}: {
  /** Stable test id, decoupled from the visible label (REQ-STATUS-027). */
  testId: string;
  label: string;
  value: string;
  /**
   * Catalog description, disclosed through the shared tooltip
   * (REQ-STATUS-056). A metric with **no** catalog entry passes nothing here
   * and renders no trigger at all — a focusable control with nothing to
   * announce is worse than none (REQ-STATUS-054).
   */
  description?: string;
  /** See :prop:`ResourceRow.breach` — the same marking, for a domain metric. */
  breach?: "degraded" | "unhealthy" | null;
  /**
   * The metric's attribution from `ComponentModel.details[key]` (REQ-STATUS-103)
   * — WHICH source produced a worst-of value, e.g.
   * `MERIDIAN_DATA/meridian-time-series-a` under "Worst consumer backlog". Rendered
   * verbatim as a muted full-width line directly beneath the value (a second
   * `<dd>` for the same `<dt>`), so a long stream/consumer name wraps instead of
   * widening the value column. Absent → no line at all; the value row is
   * unchanged byte for byte.
   */
  detail?: string;
}): React.ReactElement {
  return (
    <>
      <dt className="flex items-center gap-1 text-text-secondary">
        {label}
        {description !== undefined && (
          <DescriptionTooltip testId={testId} label={label} description={description} />
        )}
      </dt>
      <dd
        data-testid={testId}
        data-breach={breach ?? undefined}
        className={
          breach === "unhealthy"
            ? "font-medium text-meridian-status-unhealthy"
            : breach === "degraded"
              ? "font-medium text-meridian-status-degraded"
              : "text-text-primary"
        }
      >
        {value}
      </dd>
      {detail !== undefined && detail !== "" && (
        <dd
          data-testid={`${testId}-detail`}
          className="col-span-2 -mt-1 break-all font-mono text-meta text-text-muted"
        >
          {detail}
        </dd>
      )}
    </>
  );
}

/** One trend point: ``[epoch_seconds, value]``; ``null`` is a **gap**. */
export type TrendPoint = readonly [number, number | null];

/**
 * A recent-history sparkline (REQ-STATUS-064, -070, -073; design-system §Sparklines).
 *
 * # Gaps break the line — they never join across
 *
 * A `null` bucket **ends the current polyline and starts a new one**. Joining
 * across missing data would draw a straight segment asserting a continuity that
 * was never measured — on a surface whose whole job is being honest about what
 * it does not know (REQ-STATUS-073). This is why the component emits a list of
 * polylines rather than one.
 *
 * No axes, no gridlines, no point markers: a sparkline is a shape, and anything
 * inviting a value to be read off it belongs in the numeric row above it.
 *
 * # Three things the 2026-08-02 visual pass corrected
 *
 * 1. **The stroke was ``--accent`` and was therefore invisible.** That token is
 *    shadcn's interaction-state *surface* slot — ``223 21% 94%`` on light, i.e.
 *    near-white on a white panel, and near-black on dark. The brand accent LINE
 *    colour is ``--ring``. This file's sibling ``status-diagram.tsx`` carries the
 *    same warning twice; the value was inherited from the placeholder stub,
 *    which never plotted anything, so nothing ever revealed it.
 * 2. **A VISIBLE label is required, not just an accessible name.** One sparkline
 *    beside a labelled numeric row can borrow that row's label. A *stack* of ten
 *    cannot: an operator sees ten anonymous lines and cannot tell which metric is
 *    which. ``aria-label`` alone serves screen readers and abandons everyone else.
 * 3. **Width is the container's, not 160 px.** A fixed 160 px plot in a ~750 px
 *    pane wastes four fifths of the width it needs to be legible at all.
 *
 * The SVG keeps an internal coordinate system and scales via CSS, with
 * ``vector-effect="non-scaling-stroke"`` so the line stays 1.5 px at any width —
 * the same technique the topology diagram uses (REQ-STATUS-060).
 */
export function Sparkline({
  points,
  label,
  accessibleName,
  testId,
  description,
  /** Internal coordinate width; the rendered width is the container's. */
  disclosure,
  width = 600,
  height = 40,
}: {
  points: readonly TrendPoint[];
  /** The VISIBLE row label — the metric name. Kept short: the window is stated
   *  once by the section's selector, not repeated on every row. */
  label: string;
  /** The accessible name, which REQ-STATUS-070 requires to state the metric AND
   *  the window. Defaults to ``label`` where the two coincide. */
  accessibleName?: string;
  testId?: string;
  /**
   * Catalog description for this metric, disclosed on the same shared tooltip
   * the value row uses (REQ-STATUS-056). Omitted where the catalog has no
   * entry, which renders no trigger at all (REQ-STATUS-054).
   */
  description?: string;
  /**
   * A control rendered at the end of the label row — the trend detail
   * expansion toggle (REQ-STATUS-074).
   *
   * A slot rather than a built-in button: this primitive renders on surfaces
   * that offer no expansion, and a toggle that expands into nothing is worse
   * than none.
   */
  disclosure?: React.ReactNode;
  width?: number;
  height?: number;
}): React.ReactElement {
  const known = points.filter((point): point is readonly [number, number] => point[1] !== null);
  if (known.length < 2) {
    // "Not enough history yet" — a component reporting for under two buckets.
    // Deliberately NOT the pre-2026-08-01 "the timeline view is a future
    // feature", which stopped being true when this shipped.
    return (
      <div data-testid="sparkline-empty">
        <p className="text-meta text-text-secondary">{label}</p>
        <p className="text-meta italic text-text-muted">not enough history yet</p>
      </div>
    );
  }
  const values = known.map(([, value]) => value);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || 1;
  const step = points.length > 1 ? width / (points.length - 1) : width;
  // Split into runs of consecutive known values; each run is its own polyline.
  const runs: string[][] = [];
  let run: string[] = [];
  points.forEach(([, value], index) => {
    if (value === null) {
      if (run.length > 0) runs.push(run);
      run = [];
      return;
    }
    run.push(
      `${(index * step).toFixed(1)},${(height - ((value - min) / span) * height).toFixed(1)}`,
    );
  });
  if (run.length > 0) runs.push(run);

  return (
    <div className="mt-1">
      {/* The VISIBLE label. Without it a stack of trends is a stack of anonymous
          lines — see the docstring's point 2. */}
      <p
        className="flex items-center gap-1 text-meta text-text-secondary"
        data-testid={`${testId ?? "sparkline"}-label`}
      >
        <span data-testid={`${testId ?? "sparkline"}-label-text`}>{label}</span>
        {/* The SAME catalog sentence the value row above carries, on the same
            shared trigger (REQ-STATUS-056). A metric explained in one section
            and bare in another reads as two different metrics; keying both off
            `(component_kind, metric_key)` is what stops that (REQ-STATUS-053).
            No catalog entry means no trigger — a focusable control with nothing
            to announce is worse than none (REQ-STATUS-054). */}
        {description !== undefined && description !== "" && (
          <DescriptionTooltip
            testId={`${testId ?? "sparkline"}-trend`}
            label={label}
            description={description}
          />
        )}
        {disclosure}
      </p>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="none"
        role="img"
        aria-label={accessibleName ?? label}
        data-testid={testId ?? "sparkline"}
        data-runs={runs.length}
        className="block w-full"
        style={{ height }}
      >
        {runs.map((segment, index) => (
          <polyline
            key={index}
            points={segment.join(" ")}
            fill="none"
            // `--ring` is the brand accent LINE colour. NOT `--accent`, which is
            // shadcn's interaction-state SURFACE slot and paints the line in
            // roughly the panel's own background.
            stroke="hsl(var(--ring))"
            strokeWidth={1.5}
            strokeLinejoin="round"
            strokeLinecap="round"
            // Keeps the stroke 1.5 px while `preserveAspectRatio="none"` stretches
            // the coordinate system to the container (REQ-STATUS-060).
            vectorEffect="non-scaling-stroke"
          />
        ))}
      </svg>
    </div>
  );
}

/**
 * The rollup-state series, drawn as a **band of discrete segments**
 * (REQ-STATUS-070; design-system §Sparklines).
 *
 * A band and not a line, because the values are **ordinal states, not
 * magnitudes**: a line implies interpolation between `degraded` and `unhealthy`,
 * which is meaningless.
 *
 * State is encoded by **segment height as well as colour** (REQ-UI-046) — under
 * the default rollup vocabulary a worse state draws a taller bar — so the
 * series survives being read without colour, which is the same rule every other
 * status surface in the product follows.
 *
 * # Not rollup-only since 2026-08-03 (REQ-STATUS-075)
 *
 * The weight table used to live here and was keyed on rollup states, which made
 * this a rollup mark wearing a generic name. It is now a **parameter** —
 * :data:`ROLLUP_BAND` by default, so every existing caller is unchanged, and
 * :data:`BOOLEAN_BAND` for the trend detail chart. See
 * :mod:`@/system-status/band-vocabulary`.
 */
export function StateBand({
  points,
  label,
  accessibleName,
  vocabulary = ROLLUP_BAND,
  width = 600,
  height = 18,
}: {
  points: readonly (readonly [number, string])[];
  /** The VISIBLE row label. */
  label: string;
  /** Accessible name — metric + window (REQ-STATUS-070). */
  accessibleName?: string;
  /** Which state vocabulary the points speak. Defaults to the rollup states. */
  vocabulary?: BandVocabulary;
  width?: number;
  height?: number;
}): React.ReactElement {
  const slot = points.length > 0 ? width / points.length : width;
  return (
    <div className="mt-1">
      <p className="text-meta text-text-secondary" data-testid="state-band-label">
        {label}
      </p>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="none"
        role="img"
        aria-label={accessibleName ?? label}
        data-testid="state-band"
        className="block w-full"
        style={{ height }}
      >
        {points.map(([time, state], index) => {
          const weight = vocabulary.weight[state] ?? vocabulary.weight[vocabulary.fallback];
          const barHeight = height * weight;
          // A zero-weight slot still emits its rect, carrying `data-state` and
          // a zero height. Drawing nothing while REMAINING in the DOM is what
          // lets a gap be asserted as a gap rather than inferred from a
          // missing element (REQ-STATUS-073).
          return (
            <rect
              key={time}
              data-state={state}
              x={index * slot}
              y={height - barHeight}
              width={Math.max(slot - 0.5, 0.5)}
              height={barHeight}
              fill={vocabulary.fill(state)}
            />
          );
        })}
      </svg>
    </div>
  );
}

// ---- Session-local section collapse (REQ-UI-131) ----------------

/**
 * Every ``SectionShell`` id rendered by the three status Selection Details, in
 * render order: component, then interface, then connection.
 *
 * Published as the context's ``sectionIds`` for shape parity with the four
 * originally-persisted entity types. Derived from the canonical
 * ``SECTION_IDS`` table rather than re-listed here (Phase G): the same roster
 * now drives the Profile capture and the Profile editor, and two copies would
 * drift the moment a section is added (CLAUDE.md §4.4).
 */
// eslint-disable-next-line react-refresh/only-export-components -- derived constant co-located with its component; see eslint.config.js
export const STATUS_SECTION_IDS: readonly string[] = STATUS_ENTITY_DETAIL_TYPES.flatMap(
  (type) => SECTION_IDS[type],
);

/**
 * A no-op write that resolves success.
 *
 * ``setVisible`` is unreachable from a status selection — no status surface
 * renders the section-visibility gear (ADR-024 §4) and :func:`isVisible` is
 * hard ``true`` — so this exists only so a future caller degrades to
 * "everything visible" rather than crashing. It resolves rather than rejects
 * because there is no failure to report: nothing was attempted.
 */
const NO_OP_WRITE = (): Promise<SetPreferencesResult> => Promise.resolve({ status: "ok" });

/**
 * Session-local expand/collapse for the status Selection Details, captured into
 * the active Profile on save (REQ-UI-131; ADR-024 §4 amended 2026-07-31).
 *
 * ``SectionShell`` gates collapsibility purely on
 * ``const collapsible = ctx !== null`` — the status sections already render
 * through it with stable ids, they simply had no provider. This is that
 * provider, backed by ``useState`` instead of a per-toggle preferences write.
 *
 * # Three layers, in precedence order
 *
 *   1. **Session override** — what the operator toggled since this pane
 *      mounted. Held in ``useState``; nothing else reads or persists it.
 *   2. **Profile baseline** — the states the active Profile captured
 *      (``entity_details.status_*``), read *without* requiring a preferences
 *      provider so an isolated pane still renders (Phase D's contract).
 *   3. **Expanded** — a section neither layer names is expanded (REQ-UI-092:
 *      an absent key is never hidden and never collapsed by omission).
 *
 * # Session-local by default; a Profile save is the persistence path
 *
 * Status sections are triage surfaces: an operator who collapses "Resource
 * utilization" to read "Domain metrics" during an incident must not find that
 * arrangement waiting three weeks later. So a toggle writes **nothing** — it
 * never calls ``setWorkingState``, and the preferences context is read
 * optionally (:const:`PreferencesContext` directly rather than
 * ``usePreferences``, which throws) so no write path can appear by accident.
 * Saving settings as a Profile is what captures the current states: the save
 * builder folds this store's overrides into the document it persists
 * (``profiles/use-save-profile`` → :func:`foldStatusSectionOverrides`), which is
 * why each mounted store publishes a snapshot to the module-level registry.
 *
 * Applying a Profile clears the session overrides, so the loaded Profile's
 * captured states take effect on the pane already on screen rather than sitting
 * masked behind a stale triage collapse (REQ-UI-131 — "applied whenever that
 * Profile is subsequently loaded"). The active-Profile pointer's identity is the
 * signal; an unrelated working-state edit (a theme change) leaves the session
 * arrangement alone.
 *
 * **Provide-if-absent.** A connection drawer renders *inside* its host's roster,
 * which is already inside this provider. Nesting a second store there would
 * reset the drawer's arrangement every time the row was folded shut, so an
 * inherited context wins and this component renders as a pass-through. It is
 * therefore safe to wrap every status pane unconditionally.
 */
export function StatusSectionsProvider({ children }: { children: ReactNode }): React.ReactElement {
  const inherited = useEntityDetailsSectionsContext();
  // Optional read: the pane must render (and stay collapsible) with no
  // preferences provider in the tree — an isolated unit test, or any surface
  // mounted outside the app shell. ``usePreferences`` would throw.
  const preferences = useContext(PreferencesContext);
  const workingState = preferences?.workingState;
  const activeProfile = preferences?.activeProfile ?? null;
  // Overrides only: ``sectionId → expanded`` for what the operator toggled this
  // session. An id absent from the map falls through to the Profile baseline
  // and then to "expanded", so no seeding from the section roster is needed.
  const [overrides, setOverrides] = useState<ReadonlyMap<string, boolean>>(
    () => new Map<string, boolean>(),
  );
  const [storeId] = useState(claimStatusSectionStoreId);

  const baseline = useMemo(() => statusSectionExpansionFromDocument(workingState), [workingState]);

  // Publish this store's overrides for the imperative save-time capture. Only
  // the OWNING provider publishes: a deferring (nested) instance renders as a
  // pass-through and its unused state must not reach a Profile.
  const owns = inherited === null;
  useEffect(() => {
    if (!owns) {
      // Became a pass-through (a host provider mounted above): withdraw, so a
      // save can never capture state this instance no longer drives.
      retractStatusSectionOverrides(storeId);
      return;
    }
    publishStatusSectionOverrides(storeId, overrides);
  }, [owns, storeId, overrides]);
  useEffect(() => () => retractStatusSectionOverrides(storeId), [storeId]);

  // A Profile apply re-bases the pane: drop the session overrides so the newly
  // loaded Profile's captured states are what renders. Keyed on the active
  // pointer's identity (id + applied-at), not on the working state, so an
  // unrelated settings edit never discards a triage arrangement.
  const appliedKey = `${activeProfile?.profile_id ?? ""}@${activeProfile?.applied_at ?? ""}`;
  useEffect(() => {
    // Functional + identity-preserving: re-running with an already-empty map
    // (the mount case) must not cause a re-render.
    setOverrides((previous) => (previous.size === 0 ? previous : new Map<string, boolean>()));
  }, [appliedKey]);

  const value = useMemo<EntityDetailsSectionsContextValue>(
    () => ({
      sections: {
        isVisible: () => true,
        setVisible: NO_OP_WRITE,
        isExpanded: (sectionId: string) =>
          overrides.get(sectionId) ?? baseline.get(sectionId) ?? true,
        setExpanded: (sectionId: string, expanded: boolean) => {
          setOverrides((previous) => {
            const next = new Map(previous);
            next.set(sectionId, expanded);
            return next;
          });
          // The context's contract is a Promise (the four persisted types PUT
          // the working state); a session-local store has nothing in flight, so
          // it resolves immediately. REQ-UI-131 — no write is enqueued here.
          return Promise.resolve({ status: "ok" });
        },
        sectionIds: STATUS_SECTION_IDS,
      },
    }),
    [overrides, baseline],
  );

  if (inherited !== null) return <>{children}</>;
  return <EntityDetailsSectionsProvider value={value}>{children}</EntityDetailsSectionsProvider>;
}
